import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Eye, AlertTriangle, Mail, FlaskConical, UserPlus } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import { supabase } from "@/integrations/supabase/client";
import TwoFactorCard from "@/components/account/TwoFactorCard";
import LinkedInCard from "@/components/account/LinkedInCard";
import InviteFriendsCard from "@/components/profile/InviteFriendsCard";

type Visibility = "public" | "members" | "private";

const AccountSettings = () => {
  const { user, loading, signOut } = useAuth();
  const { isPartnerAdmin } = useRoles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "security";

  const [pageLoading, setPageLoading] = useState(true);
  const [visibility, setVisibility] = useState<Visibility>("public");
  const [savingVis, setSavingVis] = useState(false);
  const [researchConsent, setResearchConsent] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [disposition, setDisposition] = useState<"anonymize" | "purge">("anonymize");

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    document.title = "Account Settings | emrkt.co";
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("profile_visibility, research_consent")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.profile_visibility) {
        setVisibility(data.profile_visibility as Visibility);
      }
      setResearchConsent(Boolean((data as any)?.research_consent));
      setNewEmail(user.email ?? "");
      setPageLoading(false);
    })();
  }, [user]);

  const handleSaveVisibility = async () => {
    if (!user) return;
    setSavingVis(true);
    const { error } = await supabase
      .from("profiles")
      .update({ profile_visibility: visibility })
      .eq("user_id", user.id);
    setSavingVis(false);
    if (error) {
      toast.error(error.message || "Could not update visibility");
      return;
    }
    toast.success("Visibility updated");
  };

  const handleToggleResearchConsent = async (value: boolean) => {
    if (!user) return;
    setSavingConsent(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        research_consent: value,
        research_consent_at: value ? new Date().toISOString() : null,
      })
      .eq("user_id", user.id);
    setSavingConsent(false);
    if (error) {
      toast.error(error.message || "Could not update consent");
      return;
    }
    setResearchConsent(value);
    toast.success(value ? "Thanks — you're opted in" : "Opted out of research aggregates");
  };

  const handleChangeEmail = async () => {
    const email = newEmail.trim();
    if (!email || email === user?.email) return;
    setSavingEmail(true);
    const { error } = await supabase.auth.updateUser({ email });
    setSavingEmail(false);
    if (error) {
      toast.error(error.message || "Could not update email");
      return;
    }
    toast.success("Check both inboxes to confirm the change");
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSavingPassword(false);
    if (error) {
      toast.error(error.message || "Could not update password");
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    toast.success("Password updated");
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    const { data, error } = await supabase.functions.invoke("delete-account", {
      body: { confirmation: "DELETE", disposition },
    });
    setDeleting(false);
    if (error || (data && (data as any).error)) {
      toast.error((data as any)?.error || error?.message || "Could not delete account");
      return;
    }
    toast.success("Account deleted");
    await signOut();
    navigate("/", { replace: true });
  };

  if (loading || pageLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto max-w-3xl px-6 pt-32 pb-16">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight">Account Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your sign-in, profile visibility, and account lifecycle.
            For profile content, head to{" "}
            <Link to="/profile?tab=settings" className="text-primary underline-offset-4 hover:underline">
              My Profile → Settings
            </Link>.
          </p>
        </div>

        <Tabs
          value={initialTab}
          onValueChange={(v) => {
            const next = new URLSearchParams(searchParams);
            if (v === "security") next.delete("tab");
            else next.set("tab", v);
            setSearchParams(next, { replace: true });
          }}
        >
          <TabsList className="grid w-full grid-cols-6 sm:w-auto sm:inline-grid">
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="connections">Connections</TabsTrigger>
            <TabsTrigger value="invite">Invite</TabsTrigger>
            <TabsTrigger value="visibility">Visibility</TabsTrigger>
            <TabsTrigger value="privacy">Privacy</TabsTrigger>
            <TabsTrigger value="danger">Danger Zone</TabsTrigger>
          </TabsList>

          <TabsContent value="security" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Mail className="h-4 w-4" /> Email
                </CardTitle>
                <CardDescription>
                  Changing your email sends a confirmation link to both addresses.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <Button
                  onClick={handleChangeEmail}
                  disabled={savingEmail || !newEmail.trim() || newEmail.trim() === user.email}
                >
                  {savingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update email
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lock className="h-4 w-4" /> Password
                </CardTitle>
                <CardDescription>
                  Minimum 8 characters. Use something you don't reuse elsewhere.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={savingPassword || !newPassword || !confirmPassword}
                >
                  {savingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update password
                </Button>
              </CardContent>
            </Card>

            <TwoFactorCard autoEnroll={searchParams.get("enroll") === "1"} />
          </TabsContent>

          <TabsContent value="connections" className="mt-6 space-y-6">
            <LinkedInCard />
          </TabsContent>

          <TabsContent value="invite" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserPlus className="h-4 w-4" /> Invite friends
                </CardTitle>
                <CardDescription>
                  Share your personal link to grow the shareholder network.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <InviteFriendsCard variant="plain" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="visibility" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Eye className="h-4 w-4" /> Profile visibility
                </CardTitle>
                <CardDescription>
                  Controls who can see your public profile page and author details on projects you publish.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={visibility}
                  onValueChange={(v) => setVisibility(v as Visibility)}
                  className="space-y-3"
                >
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-3">
                    <RadioGroupItem value="public" id="vis-public" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Public</p>
                      <p className="text-xs text-muted-foreground">
                        Anyone can see your profile, including search engines and anonymous visitors.
                      </p>
                    </div>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-3">
                    <RadioGroupItem value="members" id="vis-members" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Members only</p>
                      <p className="text-xs text-muted-foreground">
                        Only signed-in emrkt members can see your profile.
                      </p>
                    </div>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-3">
                    <RadioGroupItem value="private" id="vis-private" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Private</p>
                      <p className="text-xs text-muted-foreground">
                        Only you can see your profile. Your name still appears on content you publish.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
                <Button onClick={handleSaveVisibility} disabled={savingVis}>
                  {savingVis && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save visibility
                </Button>

                {isPartnerAdmin && (
                  <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                    Affiliate directory listing is managed separately from your{" "}
                    <Link to="/affiliate/dashboard" className="text-primary underline-offset-4 hover:underline">
                      Affiliate Dashboard
                    </Link>{" "}
                    (set status to Paused to hide your listing).
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="privacy" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FlaskConical className="h-4 w-4" /> Research aggregates
                </CardTitle>
                <CardDescription>
                  Opt in to include your de-identified activity in TEM's quarterly research about
                  Alabama's emerging market. Off by default.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium">Include me in aggregated research</p>
                    <p className="text-xs text-muted-foreground">
                      We may count your sector, county, and activity type in published reports
                      and the Intelligence-tier aggregates API. We <strong>never</strong> publish your
                      name, idea text, comments, messages, or anything that could re-identify you.
                      Toggle off any time — you'll be excluded from future aggregates.{" "}
                      <Link to="/privacy#use" className="underline">Learn more</Link>.
                    </p>
                  </div>
                  <Switch
                    checked={researchConsent}
                    disabled={savingConsent}
                    onCheckedChange={handleToggleResearchConsent}
                    aria-label="Research aggregates consent"
                  />
                </label>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="danger" className="mt-6 space-y-6">
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-destructive">
                  <AlertTriangle className="h-4 w-4" /> Delete account
                </CardTitle>
                <CardDescription>
                  Permanently removes your account, profile, ideas, comments, and uploads.
                  This cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive">Delete my account</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete your emrkt account?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete your account and all linked profile data.
                        Type <span className="font-semibold">DELETE</span> below to confirm.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-3 py-2">
                      <Label className="text-sm font-medium">What should happen to your posted ideas, comments, and updates?</Label>
                      <RadioGroup value={disposition} onValueChange={(v) => setDisposition(v as "anonymize" | "purge")} className="space-y-2">
                        <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40">
                          <RadioGroupItem value="anonymize" id="disp-anon" className="mt-0.5" />
                          <div className="space-y-1">
                            <div className="text-sm font-medium">Keep my public contributions (anonymized)</div>
                            <div className="text-xs text-muted-foreground">
                              Your ideas, comments, and updates stay visible to the community, attributed to "Former member".
                              Private data (profile, votes, follows, notifications) is deleted. <Link to="/retention#ideas-projects" className="underline">Learn more</Link>
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40">
                          <RadioGroupItem value="purge" id="disp-purge" className="mt-0.5" />
                          <div className="space-y-1">
                            <div className="text-sm font-medium">Delete everything I've posted</div>
                            <div className="text-xs text-muted-foreground">
                              All your ideas, comments, updates, and responses are permanently removed along with your account.
                            </div>
                          </div>
                        </label>
                      </RadioGroup>
                    </div>
                    <Input
                      value={deleteText}
                      onChange={(e) => setDeleteText(e.target.value)}
                      placeholder="DELETE"
                      autoCapitalize="characters"
                    />
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={deleteText !== "DELETE" || deleting}
                        onClick={handleDeleteAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Permanently delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AccountSettings;